import {
  Activity,
  BarChart3,
  Boxes,
  ClipboardList,
  DollarSign,
  LayoutDashboard,
  Search,
  Settings,
  Shield,
  Wrench,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/** §15.1 — Navigasyon ağacı. Etiketler birebir şartnamedeki gibidir. */
export interface NavItem {
  label: string;
  to: string;
  /** Faz planında hangi fazda geleceği — henüz hazır olmayanlar UI'da işaretlenir. */
  phase?: number;
}

export interface NavSection {
  label: string;
  icon: LucideIcon;
  to: string;
  children?: NavItem[];
  phase?: number;
  /**
   * Menüdeki küme. Yalnızca GÖRSEL bir gruplama — rotalar, etiketler ve
   * erişim aynen duruyor.
   *
   * Sebebi: 10 üst düzey bölüm ve altlarında onlarca alt madde, düz bir liste
   * olarak "her şey aynı seviyede önemli" gibi görünüyordu ve kullanıcı aradığını
   * her seferinde baştan taramak zorunda kalıyordu. Üç kümeye ayırmak, günlük
   * işi (sipariş/envanter/fiyat) ayarlardan ve arşivden görsel olarak ayırıyor.
   */
  group?: 'islem' | 'yonetim' | 'sistem' | 'araclar';
}

/** Küme başlıkları — menüde küçük, sessiz ayraçlar olarak çıkıyor. */
/** Menüde ve sekme başlığında kısa ad: uzun etiketler üst çubukta tek satıra sığmıyordu. */
export const KISA_AD: Record<string, string> = {
  'Gösterge Paneli': 'Ana Sayfa',
  'Katalog Kısıtlamaları': 'Kısıtlamalar',
};

export const NAV_GROUPS: Array<{ id: NonNullable<NavSection['group']>; label: string }> = [
  { id: 'islem', label: 'Günlük İşler' },
  { id: 'yonetim', label: 'Yönetim' },
  { id: 'sistem', label: 'Sistem' },
  { id: 'araclar', label: 'Amazon Araçları' },
];

export const NAVIGATION: NavSection[] = [
  // Faz 0'da yer tutucu olarak erişilebilir; kartları Faz 8'de dolacak.
  { label: 'Gösterge Paneli', icon: LayoutDashboard, to: '/', phase: 0 },
  {
    label: 'Siparişler',
    icon: ClipboardList,
    to: '/siparisler',
    group: 'islem',
  },
  {
    label: 'Envanter',
    icon: Boxes,
    to: '/envanter',
    group: 'islem',
    children: [
      { label: 'Envanter Yönetimi', to: '/envanter', phase: 3 },
      { label: "ASIN'e Özel Kural", to: '/envanter/asin-kurallari', phase: 1 },
      { label: 'Katalogdan Ürün Çıkarma', to: '/envanter/urun-sil', phase: 3 },
      // 22 Eki 2026: aynı ASIN'de AMZJP'nin yönetmediği ikinci ilan
      { label: 'Tekrarlı Ürünler', to: '/envanter/tekrarli' },
      { label: 'Kargo Ücretleri', to: '/envanter/kargo' },
    ],
  },
  {
    label: 'Ürün Arama',
    icon: Search,
    to: '/urun-arama',
    group: 'islem',
    children: [
      { label: 'Yeni Arama', to: '/urun-arama/yeni' },
      { label: 'Doğrudan Ürün Ekleme', to: '/urun-arama/dogrudan-ekle' },
      { label: 'Arama Geçmişi', to: '/urun-arama/gecmis' },
      { label: 'Yükleme Havuzu', to: '/urun-arama/havuz' },
      { label: 'ASIN Bilgi Bankası', to: '/urun-arama/bilgi-bankasi' },
    ],
  },
  {
    label: 'Fiyatlandırma',
    icon: DollarSign,
    to: '/fiyatlandirma',
    group: 'islem',
    children: [
      { label: 'Kâr Kuralları', to: '/fiyatlandirma/kar-kurallari', phase: 1 },
      { label: 'Repricer Ayarları', to: '/fiyatlandirma/repricer' },
      { label: 'Repricer İzleme', to: '/fiyatlandirma/repricer-izleme' },
      { label: 'Simülasyon', to: '/fiyatlandirma/simulasyon', phase: 1 },
    ],
  },
  {
    label: 'Katalog Kısıtlamaları',
    icon: Shield,
    to: '/guvenlik',
    group: 'yonetim',
    children: [
      { label: 'Marka Kısıtlamaları', to: '/guvenlik/markalar' },
      { label: 'Ürün Kısıtlamaları', to: '/guvenlik/urunler' },
      { label: 'Kelime Kısıtlamaları', to: '/guvenlik/kelimeler' },
      { label: 'Kategori Kısıtlamaları', to: '/guvenlik/kategoriler' },
      { label: 'Satıcı Kısıtlamaları', to: '/guvenlik/saticilar' },
      { label: 'Ortak Havuz', to: '/guvenlik/ortak-havuz' },
    ],
  },
  {
    label: 'Finans',
    icon: Wallet,
    to: '/finans/kar-tablosu',
    group: 'yonetim',
    children: [
      { label: 'Kâr Tablosu', to: '/finans/kar-tablosu' },
      { label: 'Sipariş ID Defteri', to: '/finans/siparis-id' },
      { label: 'Fatura Oluşturucu', to: '/finans/fatura' },
    ],
  },
  {
    label: 'Raporlar',
    icon: BarChart3,
    to: '/raporlar',
    group: 'yonetim',
    children: [
      // 22 Eki 2026 (SellerFlash incelemesi)
      { label: 'Çok Satanlar', to: '/raporlar/cok-satanlar' },
      { label: 'BuyBox Kimde', to: '/raporlar/buybox' },
      { label: 'Kategoriler', to: '/raporlar/kategoriler' },
      { label: 'Markalar', to: '/raporlar/markalar' },
      { label: 'İşletme Raporu', to: '/raporlar/business' },
      { label: 'Satış ve Trafik', to: '/raporlar/satis-trafik' },
      { label: 'Hesap Sağlığı', to: '/raporlar/hesap-sagligi' },
      { label: 'Sorunlu Ürünler', to: '/raporlar/sorunlu-urunler' },
      { label: 'Kârlılık Analizi', to: '/raporlar/kar-analizi' },
    ],
  },
  {
    label: 'İşlem Geçmişi',
    icon: Activity,
    to: '/islem-gecmisi',
    group: 'sistem',
    children: [
      { label: 'Tüm Kayıtlar', to: '/islem-gecmisi', phase: 3 },
      { label: 'Envanter Güncellemeleri', to: '/islem-gecmisi/envanter', phase: 3 },
      { label: 'Repricer İşlemleri', to: '/islem-gecmisi/repricer' },
      { label: 'Katalog Değişiklikleri', to: '/islem-gecmisi/urun', phase: 3 },
      { label: 'Amazon Gönderimleri', to: '/islem-gecmisi/feed' },
      { label: 'Toplu İşlemler', to: '/islem-gecmisi/toplu', phase: 3 },
    ],
  },
  {
    label: 'Ayarlar',
    icon: Settings,
    to: '/ayarlar',
    group: 'sistem',
    children: [
      { label: 'Güvenlik Durumu', to: '/ayarlar/guvenlik' },
      { label: 'Amazon Bağlantısı', to: '/ayarlar/amazon-baglantisi' },
      { label: 'Mağaza & Entegrasyonlar', to: '/ayarlar/magaza', phase: 2 },
      { label: 'Envanter Güncelleme', to: '/ayarlar/envanter', phase: 1 },
      { label: 'Panel Ayarlarını Kopyala', to: '/ayarlar/panel-kopyala' },
      { label: 'Kargo / Depo', to: '/ayarlar/kargo' },
      { label: 'Hazırlama Süresi', to: '/ayarlar/handling-time' },
      { label: 'Kur Ayarları', to: '/ayarlar/kur', phase: 1 },
      { label: 'Kaynak Yönetimi', to: '/ayarlar/kaynak' },
      { label: 'Önceliklendirme', to: '/ayarlar/onceliklendirme' },
      { label: 'Bildirimler', to: '/ayarlar/bildirimler' },
      { label: 'Kullanıcılar & Güvenlik', to: '/ayarlar/kullanicilar', phase: 0 },
      { label: 'Sistem Durumu', to: '/ayarlar/sistem', phase: 0 },
    ],
  },
  /*
   * Amazon Araçları (21 Eki 2026, sahip: "tüm araçlar bir yerde toplanmış olsun; Amazon
   * araçlarını da Sistem bölümünün sağ tarafına ekle"). Rotalar DEĞİŞMEDİ — yalnızca menüdeki
   * yerleri: eski bağlantılar ve yer imleri çalışmaya devam ediyor.
   *
   * Kargo Fiyatı Çekme ve Keepa Mağaza Tarama yazılımın parçası değil: kullanıcının kendi
   * bilgisayarında çalışan programların bağlantı sayfaları (§31). Buldukları ASIN/kargo fiyatı
   * yazılımın akışlarına KENDİLİĞİNDEN girmiyor.
   */
  {
    label: 'Amazon Araçları',
    icon: Wrench,
    to: '/urun-arama/amazon-araclari',
    group: 'araclar',
    children: [
      { label: 'Mağaza / Marka / ASIN Toplama', to: '/urun-arama/amazon-araclari' },
      { label: 'Sales Rank Tarayıcı', to: '/urun-arama/bsr-tarayici' },
      { label: 'Marka Ürünü Tarayıcı', to: '/urun-arama/marka-tarayici' },
      // 29 Eki 2026: sahibin Satici_Sayisi_Cekme programı (Satıcısız ASIN Bulucu)
      { label: 'Satıcı Sayısı Tarayıcı', to: '/urun-arama/satici-sayisi' },
      // 29 Eki 2026: sahibin Magaza_Tarama_Manuel programı (Amazon.sa mağazalarının ASIN'leri)
      { label: 'Mağaza ASIN Tarayıcı', to: '/urun-arama/magaza-tarayici' },
      { label: 'İhlal Radarı', to: '/finans/ihlal-radari' },
      { label: 'Yorum Tespiti', to: '/finans/yorum-tespiti' },
      { label: 'Kargo Fiyatı Çekme', to: '/araclar/kargo-cekme' },
      { label: 'Keepa Mağaza Tarama', to: '/araclar/keepa-magaza' },
      { label: 'KSKLC Drop Eklentisi', to: '/araclar/eklenti' },
    ],
  },
];

const SAYFA_TAZELE = 'amzjp-sayfa-tazele';

/**
 * Açık ekranın adresini değiştirir ve ekranı yeniden kurar.
 *
 * Ekranlar adresi (`?f=`, `?tab=`, `?sekme=`) yalnız açılışta okuyor, yol değişmeyince de yeniden
 * kurulmuyorlar: süzgeçli envanterde menüden "Envanter Yönetimi"ne basınca adres temizleniyor,
 * liste süzgeçli kalıyordu. Kabuk bu olayda içeriği baştan kuruyor (`app-shell.tsx`).
 */
export function acikEkraniTazele(adres: string): void {
  window.history.pushState({}, '', adres);
  window.dispatchEvent(new Event(SAYFA_TAZELE));
}

export function ekranTazelemeDinle(dinleyici: () => void): () => void {
  window.addEventListener(SAYFA_TAZELE, dinleyici);
  return () => window.removeEventListener(SAYFA_TAZELE, dinleyici);
}

/** Geri/ileri tuşunu kendi dinleyicisiyle karşılayan ekranlar: kurulu kalıp adresi okuyorlar. */
const GERI_TUSUNU_KENDI_OKUYAN: ReadonlySet<string> = new Set(['/envanter']);

/**
 * Geri/ileri tuşu aynı yolun başka adresine dönünce ekran yeniden kurulsun: `acikEkraniTazele`
 * geçmişe kayıt açıyor, yol değişmediği için adres eski sekmeyi söylerken ekran yenisinde kalıyordu.
 * Başka yola dönüşte kabuğun anahtarı zaten değişiyor. Yakalama aşamasında dinleniyor: yönlendirici
 * yeni adresi henüz işlemedi, `acikYol` dönmeden önceki yolu veriyor.
 */
export function geriTusuDinle(acikYol: () => string, dinleyici: () => void): () => void {
  const geriTusu = () => {
    const yol = window.location.pathname;
    if (yol === acikYol() && !GERI_TUSUNU_KENDI_OKUYAN.has(yol)) dinleyici();
  };
  window.addEventListener('popstate', geriTusu, true);
  return () => window.removeEventListener('popstate', geriTusu, true);
}

/** Menü bağlantısı açık ekranı gösteriyorsa ve adreste süzgeç varsa ekran temiz hâline dönsün. */
export function menuBaglantisiTiklandi(
  e: { preventDefault: () => void; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean },
  to: string,
): void {
  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
  if (window.location.pathname !== to || window.location.search === '') return;
  e.preventDefault();
  acikEkraniTazele(to);
}

/**
 * Adresin menüdeki adı (sekme başlığı). En uzun eşleşen yol kazanıyor: `/envanter/asin-kurallari`
 * "Envanter" değil "ASIN'e Özel Kural" olsun. Menüde olmayan adreste ad uydurulmuyor.
 */
export function sayfaAdi(yol: string): string | null {
  const eslesen = NAVIGATION.flatMap((b) => [{ to: b.to, label: b.label }, ...(b.children ?? [])])
    .filter((m) => m.to === yol || (m.to !== '/' && yol.startsWith(`${m.to}/`)))
    .sort((x, y) => y.to.length - x.to.length)[0];
  return eslesen ? (KISA_AD[eslesen.label] ?? eslesen.label) : null;
}
